import { FC, ReactNode } from "react";
import { SeekaConfigFragment } from "~graphql/typed-document-nodes";
import { Router } from "next/router";
import { useAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import {
  FacebookPixelScript,
  FacebookPixelRouteTracker,
  MarketingGTMScript,
} from "~features/tracking/marketing/integrations";
import { pixelIdAtom } from "~features/tracking/trackingConfig";
import { useCookieConsent } from "~context/cookieConsentContext";
import { OptionalSeekaProvider, orgLevelSeeka } from "~lib/seeka";
import { useOrganization } from "~hooks/useOrganization";
import { OrganizationFeatures } from "~lib/features";

interface MarketingConsentProviderProps {
  children: ReactNode;
  pixelId?: string;
  gtmId?: string;
  seekaConfig?: SeekaConfigFragment;
  router?: Router;
}

export const MarketingConsentProvider: FC<MarketingConsentProviderProps> = ({
  children,
  pixelId,
  gtmId,
  seekaConfig,
  router,
}) => {
  const { isMarketingAllowed } = useCookieConsent();
  const { hasFeature } = useOrganization();
  const enableMarketing = hasFeature(OrganizationFeatures.CookieConsent)
    ? isMarketingAllowed()
    : true;

  useHydrateAtoms([[pixelIdAtom, pixelId]]);
  const [hydratedPixelId] = useAtom(pixelIdAtom);

  if (enableMarketing) {
    return (
      <>
        <FacebookPixelScript pixelId={pixelId} />
        <FacebookPixelRouteTracker pixelId={hydratedPixelId} router={router} />
        <MarketingGTMScript containerId={gtmId} />
        <OptionalSeekaProvider
          seekaConfig={seekaConfig}
          enabled={orgLevelSeeka(router?.pathname)}
        >
          {children}
        </OptionalSeekaProvider>
      </>
    );
  }

  return <>{children}</>;
};
